import React, { useCallback, useEffect, useState } from 'react';
import { Tappable } from '@vectord/components';
import PropTypes from 'prop-types';

import { CONTEST_COURSE_TYPE_OPTIONS } from '@classCreation/data/contestCourseTypeOptions';
import { CONTEST_TYPES } from '@classCreation/data/contestTypes';
import { CONTEST_WINDOW_DURATION_OPTIONS } from '@classCreation/data/contestWindowDurationOptions';
import { LESSON_INPUT_OPTIONS } from '@classCreation/data/lessonInputOptions';
import { LESSON_TYPES } from '@classCreation/data/lessonTypes';
import Input from 'components/Input';
import LessonResourceType from '@classCreation/propTypes/lessonResourceType';
import lessonInputPlaceholder from '@classCreation/utils/lessonInputPlaceholder';
import parseContestCourseTypeOptions from '@classCreation/utils/parseContestCourseTypeOptions';
import parseContestWindowOptions from '@classCreation/utils/parseContestWindowOptions';
import parseLessonInputValue from '@classCreation/utils/parseLessonInputValue';
import Select from 'components/Select';
import styles from './LessonInput.module.scss';

export default function LessonInput({
  lessonType,
  contestType,
  value,
  onChange,
  disabled = false,
  className,
  ...remainingDurationProps
}) {
  const isTestInputVisible = !(lessonType === LESSON_TYPES.liveLecture);
  const isContest = lessonType === LESSON_TYPES.contest;
  const { properties } = value || {};
  const { window_in_minutes: contestWindow, course_type: contestCourseType } =
    properties || {};
  const { meta } = value || {};
  const { is_group_contest: isGroupContest, test_group_id: testGroupId } =
    meta || {};
  const [testId, setTestId] = useState(value?.test_id || '');
  const [groupId, setGroupId] = useState(value?.test_group_id || '');
  const [duration, setDuration] = useState(
    value?.duration
      ? { label: value?.duration + 'Mins', value: value?.duration }
      : null
  );
  const windowValue = value?.window;
  const courseTypeValue = value?.course_type;
  const [window, setWindow] = useState(
    parseContestWindowOptions(contestWindow, windowValue)
  );
  const [courseType, setCourseType] = useState(
    parseContestCourseTypeOptions(contestCourseType, courseTypeValue)
  );
  const label = LESSON_INPUT_OPTIONS[lessonType].label;

  useEffect(() => {
    setTestId(value?.test_id || '');
    setGroupId(value?.test_group_id || '');
    setDuration(
      value?.duration
        ? { label: value?.duration + ' Mins', value: value?.duration }
        : null
    );
    setWindow(parseContestWindowOptions(contestWindow, windowValue));
    setCourseType(
      parseContestCourseTypeOptions(contestCourseType, courseTypeValue)
    );
  }, [contestWindow, value, windowValue, contestCourseType, courseTypeValue]);

  const handleIdChange = useCallback(
    (event) => {
      if (contestType === CONTEST_TYPES.group) {
        setGroupId(event.target.value);
        onChange({
          ...value,
          lesson_type: lessonType,
          test_group_id: event.target.value,
        });
      } else {
        setTestId(event.target.value);
        onChange({
          ...value,
          lesson_type: lessonType,
          test_id: event.target.value,
        });
      }
    },
    [lessonType, contestType, onChange, value]
  );

  const handleDurationChange = useCallback(
    (option) => {
      setDuration(option);
      onChange({
        ...value,
        lesson_type: lessonType,
        duration: option?.value,
      });
    },
    [lessonType, onChange, value]
  );

  const handleContestWindowChange = useCallback(
    (option) => {
      setWindow(option);
      onChange({
        ...value,
        lesson_type: lessonType,
        window: option?.value,
      });
    },
    [lessonType, onChange, value]
  );

  const handleContestCourseTypeChange = useCallback(
    (option) => {
      setCourseType(option);
      onChange({
        ...value,
        lesson_type: lessonType,
        course_type: option?.value,
      });
    },
    [lessonType, onChange, value]
  );

  return (
    <div className={className}>
      <div className="row">
        {isTestInputVisible && (
          <Input
            className={styles.slugInput}
            placeholder={lessonInputPlaceholder(lessonType, contestType)}
            value={parseLessonInputValue(
              contestType,
              isGroupContest,
              testId,
              groupId || testGroupId
            )}
            disabled={disabled}
            onChange={handleIdChange}
          />
        )}
        <Select
          className={styles.durationInput}
          placeholder={`Enter ${label} Duration`}
          value={duration}
          isDisabled={disabled}
          options={LESSON_INPUT_OPTIONS[lessonType].duration}
          onChange={handleDurationChange}
          {...remainingDurationProps}
        />
        {isContest && !(contestType === CONTEST_TYPES.group || isGroupContest) && (
          <Select
            className={styles.durationInput}
            placeholder={`Enter ${label} Window`}
            value={window}
            isDisabled={disabled}
            options={CONTEST_WINDOW_DURATION_OPTIONS}
            onChange={handleContestWindowChange}
          />
        )}
        {isContest && (contestType === CONTEST_TYPES.group || isGroupContest) && (
          <Select
            className={styles.durationInput}
            placeholder={`Select Course Type`}
            value={courseType}
            isDisabled={disabled}
            options={CONTEST_COURSE_TYPE_OPTIONS}
            onChange={handleContestCourseTypeChange}
          />
        )}
        {(groupId || testId) && (
          <Tappable
            className="link"
            href={
              isGroupContest || contestType === CONTEST_TYPES.group
                ? `/hire/test/#/edit-test-group/${testGroupId || groupId}`
                : `/hire/test/${testId}`
            }
            noHighlight
            target="_blank"
          >
            + Add Questions
          </Tappable>
        )}
      </div>
    </div>
  );
}

LessonInput.propTypes = {
  lessonType: PropTypes.oneOf(Object.values(LESSON_TYPES)),
  value: LessonResourceType,
  onChange: PropTypes.func,
  className: PropTypes.string,
};
